import moment from 'moment';

const BOOKED_FILTER_KEYS = [
    'searchQuery',
    'treatment',
    'startDate',
    'endDate',
    'contactedCount',
    'tags',
    'referral',
    'bookedWith',
    'depositPendingStatus',
    'assigneeIds',
    'noTreatment',
    'customTabId',
    'directCountFilter',
    'activePracticeIds',
];

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

export const getBookedPracticeScopeKey = (practiceId, activePracticeIds) => {
    const normalizedPracticeIds = Array.isArray(activePracticeIds)
        ? [...new Set(activePracticeIds.map((id) => String(id)))].sort()
        : [];
    return `${practiceId == null ? '' : String(practiceId)}|${normalizedPracticeIds.join(',')}`;
};

const BOOKED_TRANSIENT_REQUEST_KEYS = new Set([
    'id',
    'filterStatus',
    'type',
    'resultOnly',
    'forceRefetch',
    'priority',
]);

export const mergeBookedTableRequest = (current = {}, incoming = {}) => {
    const next = { ...current };
    Object.keys(incoming).forEach((key) => {
        if (BOOKED_TRANSIENT_REQUEST_KEYS.has(key)) return;
        // Existing callers frequently include undefined placeholders. Treat only
        // an omitted/undefined value as "preserve"; null and empty collections
        // remain explicit clears.
        if (incoming[key] !== undefined) next[key] = incoming[key];
    });
    return next;
};

export const isCompleteBookedTableRequest = (payload = {}) => (
    payload.limit != null
    && Number(payload.limit) > 0
    && payload.offset != null
    && Number(payload.offset) >= 0
);

export const mergeBookedTableFilters = (current = {}, incoming = {}) => {
    const next = { ...current };
    BOOKED_FILTER_KEYS.forEach((key) => {
        if (hasOwn(incoming, key)) next[key] = incoming[key];
    });
    return next;
};

export const mergeBookedPracticeFilters = (current, practiceId, incoming = {}) => ({
    practiceId,
    filters: mergeBookedTableFilters(
        current?.practiceId === practiceId ? current.filters : {},
        incoming,
    ),
});

// Task L: resolves the NEXT bookedWidgetFilter scope for a widget card/chip
// click, given the CURRENTLY active scope. Centralised here (not inline in
// NewLead.jsx's onWidgetClick) so the "Upcoming" card's two-level toggle —
// body scope 'upcoming' plus its 'upcoming_week' chip refinement, both
// belonging to ONE card — is unit-testable without NewLead.jsx's huge
// dependency graph, and so the widget and the click-resolution logic can't
// silently drift apart.
//
// 'upcoming' (card body): toggles the WHOLE card off (-> null) if either
//   'upcoming' or 'upcoming_week' is currently active; otherwise selects
//   'upcoming'.
// 'upcoming_week' (the card's chip): toggles back to the card's own
//   'upcoming' scope if it's already active; otherwise selects
//   'upcoming_week'. It does NOT fall all the way to null — the chip is a
//   refinement of the card, not an independent toggle.
// Any other key (e.g. 'past', 'awaiting'): the original simple toggle —
//   clicking the already-active one clears to null, otherwise selects it.
export const resolveBookedWidgetClickTarget = (clickedKey, currentScope) => {
    if (clickedKey === 'upcoming') {
        const isUpcomingFamilyActive = currentScope === 'upcoming' || currentScope === 'upcoming_week';
        return isUpcomingFamilyActive ? null : 'upcoming';
    }
    if (clickedKey === 'upcoming_week') {
        return currentScope === 'upcoming_week' ? 'upcoming' : 'upcoming_week';
    }
    return currentScope === clickedKey ? null : clickedKey;
};

export const applyBookedWidgetScope = (payload = {}, currentScope = null) => {
    const hasExplicitScope = hasOwn(payload, 'bookedWidgetFilter')
        && payload.bookedWidgetFilter !== undefined;
    const scope = hasExplicitScope ? payload.bookedWidgetFilter : currentScope;
    return {
        scope: scope || null,
        payload: hasExplicitScope ? payload : { ...payload, bookedWidgetFilter: scope || null },
    };
};

const laterDate = (left, right) => (!left || (right && right > left) ? right : left);
const earlierDate = (left, right) => (!left || (right && right < left) ? right : left);

export const resolveBookedWidgetDateWindow = ({
    scope,
    startDate,
    endDate,
    today = moment().format('YYYY-MM-DD'),
} = {}) => {
    if (!scope) return null;

    const userStart = startDate || null;
    const userEnd = endDate || null;
    let lower = userStart;
    let upper = userEnd;

    if (scope === 'upcoming') {
        // Task L (2026-10-03): "Upcoming" now means ALL future bookings, not just
        // the next 7 days — no upper bound unless the user supplied their own
        // endDate. buildBookedWidgetDateQuery turns a lower-bound-only window
        // into `__gte` (no `__range`/far-future end needed — the existing 'past'
        // scope already proves the BE accepts a one-sided bound on this same
        // field via `__lte`, and `__gte` is the symmetric counterpart on the
        // same lookup).
        lower = laterDate(userStart, today);
        upper = userEnd;
    } else if (scope === 'upcoming_week') {
        // The OLD "upcoming" definition — today through today+7 — demoted to its
        // own scope key so it can live as the "N this week" chip inside the
        // (now all-upcoming) "Upcoming" card.
        lower = laterDate(userStart, today);
        upper = earlierDate(userEnd, moment(today).add(7, 'days').format('YYYY-MM-DD'));
    } else if (scope === 'past') {
        upper = earlierDate(userEnd, moment(today).subtract(1, 'day').format('YYYY-MM-DD'));
    } else {
        return null;
    }

    return {
        startDate: lower,
        endDate: upper,
        empty: Boolean(lower && upper && lower > upper),
    };
};

export const buildBookedWidgetDateQuery = ({ scope, startDate, endDate, today } = {}) => {
    const window = resolveBookedWidgetDateWindow({ scope, startDate, endDate, today });
    if (!window) return '';
    if (window.empty) return '&id=0';
    if (window.startDate && window.endDate) {
        return `&lead_treatments__appointments__datetime__range=${window.startDate} 00:00:00,${window.endDate} 23:59:00`;
    }
    if (window.startDate) {
        return `&lead_treatments__appointments__datetime__gte=${window.startDate} 00:00:00`;
    }
    return `&lead_treatments__appointments__datetime__lte=${window.endDate} 23:59:00`;
};

export const buildBookedWidgetCountRequest = (
    practiceId,
    filters,
    scope,
    { forceRefetch = false, priority = undefined } = {},
) => ({
    ...filters,
    id: practiceId,
    limit: 1,
    offset: 0,
    order: filters?.order || 'desc',
    orderId: filters?.orderId || 'created_at',
    contactedCount: filters?.contactedCount ?? 'All',
    filterStatus: 'CONSULTATION_BOOKED',
    type: 'ConsultantBooked',
    bookedWidgetFilter: scope,
    resultOnly: true,
    ...(forceRefetch ? { forceRefetch: true } : {}),
    ...(priority ? { priority } : {}),
});
